import { createContext } from 'react';
import { decryptWithAKey } from 'app/services/utils/encrypt';
import { getAllQueryParam, getQueryParam } from 'app/services/utils/url';
import { getStorageItem, removeStorageItem, setStorageItem } from '../storage/storage';
import { getToken, logout, parseIdToken } from './authentication';

export const AuthContext = createContext<AuthContext.Context>(null);

// Resolve test impersonation once per full page load (not on every React re-render),
// so a value persisted from the URL param isn't wiped by later client-side navigations.
let impersonationResolved = false;

export default function Auth({ children }: { children?: React.ReactNode }): JSX.Element {
  const oktaToken = getQueryParam('okta_token');
  if (oktaToken) {
    const allParamsWithoutOkta = getAllQueryParam();
    allParamsWithoutOkta.delete('okta_token');
    setStorageItem('idToken', oktaToken, 'local');
    setStorageItem('userLoginTimestamp', new Date().toString(), 'session');
    setStorageItem('isAuthenticated', true, 'session');
    const pagePathWithoutOktaToken = `${window.location.pathname}?${allParamsWithoutOkta.toString()}`;
    window.history.pushState({}, document.title, pagePathWithoutOktaToken);
  }

  // Test impersonation: read impersonate param from URL and persist to sessionStorage (non-prod only).
  // Runs once per page load: if the param is present, persist it; if absent on a fresh load,
  // clear any stale value so impersonation doesn't leak into a non-impersonated session.
  if (process.env.ENV !== 'prod' && !impersonationResolved) {
    impersonationResolved = true;
    const impersonateParam = getQueryParam('impersonate');
    if (impersonateParam) {
      sessionStorage.setItem('x-test-impersonate', impersonateParam);
      const allParams = getAllQueryParam();
      allParams.delete('impersonate');
      const cleanPath = `${window.location.pathname}?${allParams.toString()}`;
      window.history.pushState({}, document.title, cleanPath);
    } else {
      sessionStorage.removeItem('x-test-impersonate');
    }
  }

  const getUserId = (): string => {
    const idToken = getStorageItem('idToken', 'local');

    if (idToken) {
      const parsedIdToken = parseIdToken(idToken as JWT);

      return parsedIdToken.myAccountId;
    }

    return null;
  };

  const checkIsAuthenticated = () => {
    return getStorageItem('isAuthenticated', 'session') === true;
  };

  const userInfo = () => {
    const key = getUserId();
    return key ? JSON.parse(decryptWithAKey(getStorageItem('userInfo', 'session'), key).toString()) : null;
  };

  const authenticateUser = async (urlParams) => {
    let userAuthenticated = false;
    const token = await getToken(urlParams);
    if (token) userAuthenticated = true;
    setStorageItem('isAuthenticated', true, 'session');
    return userAuthenticated;
  };

  const removeUserInfo = async () => {
    removeStorageItem('userInfo', 'session');
    removeStorageItem('isAuthenticated', 'session');
    await logout();
  };

  return (
    <AuthContext.Provider value={{ checkIsAuthenticated, authenticateUser, userInfo, removeUserInfo, getUserId }}>
      {children}
    </AuthContext.Provider>
  );
}
